import { LinearProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import type { CreateCalculationRequest } from "../calculations/calculationsApi";
import { initializeCalculationFromOrder, initializeCalculationFromPackage } from "../calculations/calculationUtils";
import type { Response } from "../calculations/useCalculationFromQuery";
import { useCalculationFromQuery } from "../calculations/useCalculationFromQuery";
import { useCreateCalculation } from "../calculations/useCreateCalculation";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import type { CustomerListItem } from "../customers/customersApi";
import type { Currency } from "../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import type { SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import type { CalculationDraft } from "./CalculationDraft";
import { emptyDraft } from "./CalculationDraft";
import { CalculationEdit } from "./CalculationEdit";
import { useHandleSaveVersion } from "./utils/useHandleSaveVersion";

export interface DuplicateFromState {
  readonly name: string;
  readonly startDate: string;
  readonly participantsNumber: { readonly numberOfPaid: number; readonly numberOfStaff: number };
  readonly products: ReadonlyArray<{
    readonly id: string;
    readonly daysShift: number;
    readonly options: ReadonlyArray<{ readonly id: string; readonly quantity: number }>;
  }>;
  readonly currency?: Currency;
  readonly margin?: number;
  readonly packageId?: string;
  readonly note?: string;
  readonly customer: { readonly id: string; readonly name: string } | null;
  readonly isPackageVersion: boolean;
}

export interface NewCalculationPageLocationState {
  readonly duplicateFrom?: DuplicateFromState;
}

function draftFromDuplicate(from: DuplicateFromState): CalculationDraft {
  return {
    calculationId: undefined,
    basicData: {
      name: from.name,
      startDate: dayjs(from.startDate),
      margin: from.margin ?? 0,
      peopleCount: from.participantsNumber.numberOfPaid,
      freePeopleCount: from.participantsNumber.numberOfStaff,
      additionalInfo: from.note ?? "",
      isPackageVersion: from.isPackageVersion,
      customer: from.customer as CustomerListItem | undefined,
    },
    currency: from.currency,
    packageId: from.packageId,
    products: from.products.map((p) => ({
      id: p.id,
      daysShift: p.daysShift,
      options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
    })),
  };
}

function buildRequestFromDraft(draft: CalculationDraft, customerId?: string) {
  const startDate = draft.basicData.startDate!;
  return {
    customerId: draft.basicData.customer?.id ?? customerId,
    name: draft.basicData.name,
    versionName: draft.versionName,
    startDate: startDate.format(ISO_DATE_FORMAT),
    participantsNumber: {
      numberOfPaid: draft.basicData.peopleCount,
      numberOfStaff: draft.basicData.freePeopleCount,
    },
    products: draft.products.map((p) => ({
      id: p.id,
      date: startDate.add(p.daysShift, "days").format(ISO_DATE_FORMAT),
      options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price })),
    })),
    currency: draft.packageId ? undefined : draft.currency,
    margin: draft.packageId ? undefined : draft.basicData.margin,
    packageId: draft.packageId,
    type: draft.basicData.isPackageVersion ? ("PACKAGE" as const) : ("DEFAULT" as const),
    note: draft.basicData.additionalInfo || undefined,
  };
}

export function NewCalculationPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const locationState = useLocation().state as NewCalculationPageLocationState | null;
  const { context } = useAuthContext();
  const calculationFromQueryResponse = useCalculationFromQuery();
  const { onCreate } = useCreateCalculation();
  const tomorrow = dayjs().add(1, "day").format(ISO_DATE_FORMAT);
  const defaultStartDate = dayjs(tomorrow);

  const [initialDraft, setInitialDraft] = useState(
    createInitialDraft(defaultStartDate, locationState, calculationFromQueryResponse),
  );

  useEffect(() => {
    setInitialDraft(createInitialDraft(defaultStartDate, locationState, calculationFromQueryResponse));
  }, [calculationFromQueryResponse.isLoading]);

  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);
  const [pendingDraft, setPendingDraft] = useState<CalculationDraft | null>(null);

  const isLoading = calculationFromQueryResponse.isLoading || !context;

  const doCreate = async (draft: CalculationDraft) => {
    const isCustomer = context?.role === "CUSTOMER";

    if (!isCustomer && !draft.basicData.customer) {
      enqueueSnackbar("Nie można utworzyć kalkulacji. Wybierz klienta.", {
        variant: "error",
      });
      return;
    }
    const customerId = isCustomer ? context.customerId : undefined;
    const request = buildRequestFromDraft(draft, customerId) as CreateCalculationRequest;
    const { id } = await onCreate(request, context!.role);
    navigate(`/calculations/${id}`);
  };

  const { handleSaveVersion } = useHandleSaveVersion(doCreate, setDiscrepancyModal, setPendingDraft);

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <>
      <CalculationEdit onSubmitVersion={handleSaveVersion} value={initialDraft} context={context} isEditMode={true} />
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        onConfirm={async () => {
          if (pendingDraft) {
            await doCreate(pendingDraft);
          }
          setDiscrepancyModal(null);
          setPendingDraft(null);
        }}
        onCancel={() => {
          setDiscrepancyModal(null);
          setPendingDraft(null);
        }}
      />
    </>
  );
}
function createInitialDraft(
  defaultStartDate: dayjs.Dayjs,
  locationState: NewCalculationPageLocationState | null,
  calculationFromQueryResponse: Response,
) {
  if (locationState?.duplicateFrom) {
    return draftFromDuplicate(locationState.duplicateFrom);
  } else if (calculationFromQueryResponse.type === "FROM_PACKAGE" && calculationFromQueryResponse.data) {
    const fromPackage = initializeCalculationFromPackage(calculationFromQueryResponse.data, defaultStartDate);
    return {
      ...emptyDraft,
      packageId: fromPackage.packageId,
      currency: fromPackage.currency,
      basicData: { ...emptyDraft.basicData, startDate: defaultStartDate, margin: fromPackage.margin ?? 0 },
      products: (fromPackage.products ?? []).map((p) => ({
        id: p.id,
        daysShift: p.daysShift ?? 0,
        options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
      })),
    };
  } else if (calculationFromQueryResponse.type === "FROM_ORDER" && calculationFromQueryResponse.data) {
    const fromPackage = initializeCalculationFromOrder(calculationFromQueryResponse.data);
    return {
      ...emptyDraft,
      currency: fromPackage.currency,
      basicData: {
        ...emptyDraft.basicData,
        startDate: fromPackage.startDate ? dayjs(fromPackage.startDate, ISO_DATE_FORMAT) : defaultStartDate,
        margin: fromPackage.margin ?? 0,
      },
      products: (fromPackage.products ?? []).map((p) => ({
        id: p.id,
        daysShift: p.daysShift ?? 0,
        options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
      })),
    };
  }
  return {
    ...emptyDraft,
    basicData: { ...emptyDraft.basicData, startDate: defaultStartDate },
  };
}
